import React, { useEffect, useMemo, useState } from 'react';
import {
    useGetClassStaffQuery,
    useSetClassStaffMutation,
    useGetLocationMembersQuery,
} from '../../store/api/localApi';
import { useToast } from '../../components/Toast';
import { useLocationContext } from '../../hooks/useLocationContext';
import Modal from '../../components/Modal';
import { UsersIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Props {
    classId: string;
    locationId: string;
    isAdmin: boolean;
}

/**
 * Class detail panel section: who's assigned to teach this class.
 * Admin can edit via a checkbox modal listing all staff at the location.
 */
const AssignedStaffSection: React.FC<Props> = ({ classId, locationId, isAdmin }) => {
    const { effectiveOrgId } = useLocationContext();
    const { data: classStaffData } = useGetClassStaffQuery(classId);
    const assignedStaff = classStaffData?.staff || [];
    const [showEdit, setShowEdit] = useState(false);

    return (
        <div className="bg-white shadow rounded-lg" data-testid="assigned-staff">
            <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                        <UsersIcon className="h-5 w-5 text-gray-400" />
                        Assigned Staff
                        <span className="text-sm font-normal text-gray-500">({assignedStaff.length})</span>
                    </h3>
                    {isAdmin && (
                        <button
                            onClick={() => setShowEdit(true)}
                            data-testid="edit-class-staff-btn"
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                        >
                            <PlusIcon className="h-4 w-4 mr-1" /> Edit
                        </button>
                    )}
                </div>

                {assignedStaff.length === 0 ? (
                    <p className="text-sm text-gray-500">
                        No staff assigned. {isAdmin ? 'Click Edit to add staff.' : ''}
                    </p>
                ) : (
                    <ul className="space-y-1">
                        {assignedStaff.map(s => (
                            <li
                                key={s.id}
                                className="flex items-center gap-2 text-sm text-gray-700"
                                data-testid={`assigned-staff-${s.id}`}
                            >
                                <div className="h-6 w-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs font-medium text-primary-600">
                                        {(s.firstName?.[0] || '?').toUpperCase()}{(s.lastName?.[0] || '').toUpperCase()}
                                    </span>
                                </div>
                                <span>{s.firstName} {s.lastName}</span>
                                <span className="text-xs text-gray-400">{s.email}</span>
                                {s.isStaffHere === false && (
                                    <span
                                        data-testid={`assigned-staff-${s.id}-stale`}
                                        className="inline-flex flex-shrink-0 items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                                        title="Still on this class, but no longer a staff member at this location. Edit to remove."
                                    >
                                        No longer staff here
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {showEdit && (
                <EditAssignedStaffModal
                    isOpen={showEdit}
                    onClose={() => setShowEdit(false)}
                    classId={classId}
                    orgId={effectiveOrgId || ''}
                    locationId={locationId}
                    currentlyAssigned={assignedStaff}
                />
            )}
        </div>
    );
};

type AssignedStaffMember = {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    isStaffHere?: boolean;
};

const EditAssignedStaffModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    classId: string;
    orgId: string;
    locationId: string;
    currentlyAssigned: AssignedStaffMember[];
}> = ({ isOpen, onClose, classId, orgId, locationId, currentlyAssigned }) => {
    const { showSuccess, showError } = useToast();
    const currentlyAssignedIds = useMemo(() => currentlyAssigned.map(s => s.id), [currentlyAssigned]);
    const { data: locationMembers } = useGetLocationMembersQuery(
        { orgId, locationId },
        { skip: !isOpen || !orgId },
    );
    const [setClassStaff, { isLoading }] = useSetClassStaffMutation();
    const [selected, setSelected] = useState<Set<string>>(new Set(currentlyAssignedIds));

    useEffect(() => {
        setSelected(new Set(currentlyAssignedIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentlyAssignedIds.join(',')]);

    // Only STAFF and LOCATION_ADMIN are eligible — parents and pending invites
    // shouldn't show up in the picker.
    const eligible = useMemo(() => {
        const members = locationMembers?.members || [];
        return members.filter((m: any) =>
            !m.isPendingInvite && (m.role === 'STAFF' || m.role === 'LOCATION_ADMIN'),
        );
    }, [locationMembers]);

    // People still on the class who are not in the picker — usually they have
    // left the location (or are an old duplicate account), occasionally a
    // multi-location account the members list does not carry. They used to
    // ride along invisibly in every save and fail it; now they are listed,
    // ticked, so the admin can take them off. The "no longer staff" tag is the
    // server's verdict (`isStaffHere`), not "missing from the picker".
    const staleAssigned = useMemo(() => {
        const eligibleIds = new Set(eligible.map((m: any) => m.userId));
        return currentlyAssigned.filter(s => !eligibleIds.has(s.id));
    }, [currentlyAssigned, eligible]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await setClassStaff({ classId, userIds: Array.from(selected) }).unwrap();
            showSuccess('Staff assignments updated');
            onClose();
        } catch (err: any) {
            // The server names the person it refused; the code alone told an
            // admin nothing about which of forty staff was the problem.
            showError(err.data?.message || err.data?.error || 'Failed to update assignments');
        }
    };

    const toggle = (userId: string, checked: boolean) => {
        const next = new Set(selected);
        if (checked) next.add(userId); else next.delete(userId);
        setSelected(next);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Edit Assigned Staff" testId="edit-class-staff-modal" maxWidth="sm">
            <form onSubmit={handleSave} className="space-y-4">
                <p className="text-sm text-gray-600">Pick the staff members assigned to this class.</p>
                <div className="max-h-64 overflow-y-auto rounded-md border border-gray-300 p-2 space-y-1">
                    {eligible.map((m: any) => (
                        <label key={m.userId} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid={`assign-staff-${m.userId}`}
                                checked={selected.has(m.userId)}
                                onChange={(e) => toggle(m.userId, e.target.checked)}
                            />
                            <span>{m.firstName} {m.lastName}</span>
                            <span className="text-xs text-gray-400">{m.email}</span>
                        </label>
                    ))}
                    {eligible.length === 0 && (
                        <p className="text-xs text-gray-500">No staff available at this location.</p>
                    )}
                    {staleAssigned.map(s => (
                        <label key={s.id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid={`assign-staff-${s.id}`}
                                checked={selected.has(s.id)}
                                onChange={(e) => toggle(s.id, e.target.checked)}
                            />
                            <span className="whitespace-nowrap">{s.firstName} {s.lastName}</span>
                            <span className="text-xs text-gray-400 truncate min-w-0">{s.email}</span>
                            {s.isStaffHere === false && (
                                <span
                                    data-testid={`assign-staff-${s.id}-stale`}
                                    className="inline-flex flex-shrink-0 items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                                >
                                    No longer staff here
                                </span>
                            )}
                        </label>
                    ))}
                </div>
                <div className="flex justify-end gap-3 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        <XMarkIcon className="h-4 w-4 inline mr-1" /> Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isLoading}
                        data-testid="save-class-staff-btn"
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                    >
                        {isLoading ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default AssignedStaffSection;
